import { useEffect, useState } from 'react'
import { ApiRequestError } from '../../../lib/api'
import { quotaGateFromExtraction } from '../../../lib/quota'
import { type ExtractionSummary, useOnboardingV2Store } from '../../../stores/onboardingV2Store'
import QuotaGate from '../../QuotaGate'
import OnboardingLayout from './OnboardingLayout'
import PrimaryButton from './PrimaryButton'
import { stepsForMode } from './steps'

const POLL_MS = 2000

function stageLabel(e: ExtractionSummary | null | undefined): string {
  if (!e) return 'Starting…'
  if (e.stage === 'dedup' || e.status === 'deduplicating') return 'Organising what we found…'
  if (e.candidates_found > 0) return `Found ${e.candidates_found} so far…`
  if (e.sources_total > 0) return `Reading ${e.sources_total} ${e.sources_total === 1 ? 'source' : 'sources'}…`
  return 'Reading your material…'
}

// Extraction Wait: the user saved their profile before background extraction
// finished. We poll /v2/state until extraction is ready, then "Continue to
// review" advances the state (POST /v2/continue-to-review). Type T thin-Q&A is
// not included — those endpoints are deferred (see docs/TODO.md).
export default function ExtractionWaitScreen() {
  const capabilityMode = useOnboardingV2Store((s) => s.state?.capability_mode ?? 'managed')
  const extraction = useOnboardingV2Store((s) => s.state?.extraction)
  const fetchState = useOnboardingV2Store((s) => s.fetchState)
  const continueToReview = useOnboardingV2Store((s) => s.continueToReview)
  const retryExtraction = useOnboardingV2Store((s) => s.retryExtraction)

  const [advancing, setAdvancing] = useState(false)
  const [retrying, setRetrying] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const status = extraction?.status
  const ready = status === 'ready'
  const failed = status === 'failed'
  // A quota-terminated extraction is non-retryable — show the gate, not "Try again"
  // (otherwise the retry hits the same wall: the infinite loop). The mapper is the
  // only place that inspects error_reason.
  const quotaGate = quotaGateFromExtraction(extraction)
  // An invalid/revoked BYO key is fixed in place (inline form in the gate); on
  // success we re-run extraction, which reuses the already-uploaded material —
  // the user loses nothing. No navigation to Settings, no FlowSession change.
  const keyInvalid = quotaGate?.kind === 'byo_key_invalid'

  useEffect(() => {
    if (ready || failed) return
    const id = setInterval(() => {
      void fetchState()
    }, POLL_MS)
    return () => clearInterval(id)
  }, [ready, failed, fetchState])

  const handleContinue = async () => {
    setAdvancing(true)
    setError(null)
    try {
      await continueToReview()
      // fetchState() inside continueToReview advances orchestration_state; the
      // page re-renders to Review and this screen unmounts.
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.error.message : 'Could not continue.')
      setAdvancing(false)
    }
  }

  const handleRetry = async () => {
    setRetrying(true)
    setError(null)
    try {
      await retryExtraction()
      // Resets extraction to pending and re-launches; polling resumes below.
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.error.message : 'Could not retry.')
    } finally {
      setRetrying(false)
    }
  }

  return (
    <OnboardingLayout steps={stepsForMode(capabilityMode)} activeKey="review">
      <div className="mx-auto w-full max-w-[480px] text-center">
        <h1 className="font-serif text-[28px] font-semibold text-ob-ink md:text-3xl">
          Analysing your CVs
        </h1>
        <p className="mt-2 font-body text-base text-ob-secondary">
          We're reading everything you shared and pulling out your proof. This usually takes a
          moment.
        </p>

        <div className="mt-10 flex flex-col items-center gap-4">
          {quotaGate ? (
            <QuotaGate
              gate={quotaGate}
              variant="card"
              onKeyResolved={keyInvalid ? handleRetry : undefined}
            />
          ) : failed ? (
            <>
              <p className="font-body text-ob-error" role="alert">
                Something went wrong reading your CVs.
              </p>
              <PrimaryButton disabled={retrying} onClick={() => void handleRetry()}>
                {retrying ? 'Retrying…' : 'Try again'}
              </PrimaryButton>
            </>
          ) : ready ? (
            <p className="flex items-center gap-2 font-body text-ob-ink">
              <span className="material-symbols-outlined text-[20px] text-ob-accent" aria-hidden="true">
                check_circle
              </span>
              Done reading your CVs — ready to review.
            </p>
          ) : (
            <>
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-ob-secondary/30 border-t-ob-ink" />
              <p className="font-body text-sm text-ob-secondary">{stageLabel(extraction)}</p>
            </>
          )}
        </div>

        {error && (
          <p className="mt-4 font-body text-sm text-ob-error" role="alert">
            {error}
          </p>
        )}

        {!failed && (
          <div className="mt-10">
            <PrimaryButton className="w-full" disabled={!ready || advancing} onClick={() => void handleContinue()}>
              {advancing ? 'Loading review…' : ready ? 'Continue to review →' : 'Still reading…'}
            </PrimaryButton>
          </div>
        )}
      </div>
    </OnboardingLayout>
  )
}
